/**
 * דף צור קשר — TASKS 4.1 · PRD 8.1: "טלפון · WhatsApp · דוא"ל · שעות פעילות · טופס".
 *
 * ⚠️ **הטופס כאן פותח `mailto:` בצד הלקוח בלבד — ⛔ אין endpoint שרת.**
 *    זרימת בקשת ההצעה האמיתית (עגלה → פרטים → PDF) נכנסת בשלבים 4.3/5.2;
 *    זה עמוד "צור קשר" כללי, לא חלק מהעגלה. בניית backend ייעודי לטופס
 *    כללי הייתה חורגת מהיקף 4.1.
 *
 * ⚠️ **שעות פעילות קבועות טרם נמסרו מטל** (כתובת ומספר עוסק מורשה כבר
 *    נסגרו — CLAUDE.md סעיף 7). ⛔ לא להמציא שעות — הניסוח כאן מפנה
 *    לוואטסאפ במקום שעון קבוע, ומתעדכן כשהמידע יגיע.
 */
import { type FormEvent, useState } from 'react';
import { EnvelopeSimple, Phone, WhatsappLogo } from '@phosphor-icons/react';
import {
  EMAIL,
  EMAIL_HREF,
  PHONE_DISPLAY,
  PHONE_TEL_HREF,
  PHONE_WHATSAPP_HREF,
} from '../content/businessInfo.js';
import { TintedSection } from '../components/TintedSection.js';

/** ⭐ מופרד מ-`handleSubmit` כדי שיהיה ניתן לבדיקה בלי לגעת ב-`window.location` (jsdom אינו תומך בניווט אמיתי). */
export function buildContactMailtoHref(name: string, contact: string, message: string): string {
  const subject = encodeURIComponent(`פנייה מהאתר - ${name || 'ללא שם'}`);
  const body = encodeURIComponent(`שם: ${name}\nפרטי יצירת קשר: ${contact}\n\n${message}`);
  return `${EMAIL_HREF}?subject=${subject}&body=${body}`;
}

export function ContactPage() {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    window.location.href = buildContactMailtoHref(name, contact, message);
  }

  return (
    // ⭐ 16.08.2026 - `TintedSection` (כיוון עיצוב "ג'") משותף לכל הדפים
    // הראשיים בניווט, לבקשת אריאנה.
    <TintedSection>
      <div className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-3xl font-bold">צור קשר</h1>

        {/* ⭐ DESIGN.md 5.9 — "כרטיס פרטי קשר... + כפתור וואטסאפ". ⚠️ ⛔ בלי שורת
          "אזור שירות" — נועם היא B2B בלי הגבלה גאוגרפית מתועדת (PRD), ואין
          לנו ניסוח מאושר להמציא (סעיף 3, "עובדות אמיתיות בלבד"). */}
        <div
          className="mt-8 max-w-md rounded-lg border border-line bg-surface p-6"
          style={{ boxShadow: 'var(--shadow-card)' }}
        >
          <h2 className="sr-only">פרטי יצירת קשר</h2>
          <ul className="space-y-3">
            <li>
              <a
                href={PHONE_TEL_HREF}
                className="text-link inline-flex items-center gap-2 font-semibold"
              >
                <Phone
                  size={18}
                  weight="regular"
                  aria-hidden="true"
                  style={{ color: 'var(--color-copper)' }}
                />
                <span className="ltr-inline">{PHONE_DISPLAY}</span>
              </a>
            </li>
            <li>
              <a
                href={EMAIL_HREF}
                className="text-link inline-flex items-center gap-2 font-semibold"
              >
                <EnvelopeSimple
                  size={18}
                  weight="regular"
                  aria-hidden="true"
                  style={{ color: 'var(--color-copper)' }}
                />
                <span className="ltr-inline">{EMAIL}</span>
              </a>
            </li>
          </ul>
          <a
            href={PHONE_WHATSAPP_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary mt-4"
          >
            <WhatsappLogo size={18} weight="regular" aria-hidden="true" />
            שיחת וואטסאפ
          </a>
        </div>

        <p className="text-muted mt-6 text-sm">
          זמינים בוואטסאפ ובטלפון בשעות הפעילות הרגילות, ונחזור לכל פנייה בהקדם האפשרי.
        </p>

        {/* ⭐ לבקשת אריאנה 16.08.2026 - הטופס יושב על כרטיס לבן, ⛔ לא ישירות
            על רצועת הנחושת - אחרת הרקע הצבוע פוגע בקריאות השדות. אותם
            טוקנים כמו כרטיס פרטי הקשר למעלה. */}
        <div
          className="bg-canvas border-line mt-10 max-w-lg rounded-lg border p-6"
          style={{ boxShadow: 'var(--shadow-card)' }}
        >
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <h2 className="text-xl font-bold">השאירו פרטים ונחזור אליכם</h2>

            <div>
              <label htmlFor="contact-name" className="block text-sm font-semibold">
                שם*
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                aria-required="true"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="mt-1 min-h-11 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="contact-details" className="block text-sm font-semibold">
                טלפון או דוא"ל לחזרה*
              </label>
              <input
                id="contact-details"
                name="contact"
                type="text"
                required
                aria-required="true"
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                className="mt-1 min-h-11 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm font-semibold">
                הודעה
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={4}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                className="mt-1 w-full rounded-md border border-line px-3 py-2"
              />
            </div>

            <button type="submit" className="btn btn-cta">
              שליחה בדוא"ל
            </button>
          </form>
        </div>
      </div>
    </TintedSection>
  );
}
